import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Card, CardContent, Typography, Box } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import DownloadIcon from '@mui/icons-material/Download';
import DepartmentWiseProductionReportFilter from '../../components/report/DepartmentWiseProductionReportFilter';
import DepartmentWiseProductionReportTable from '../../components/report/DepartmentWiseProductionReportTable';
import { getDepartmentWiseProductionReport } from '../../redux/actions/reports/reportsAction';
import ReportTitle from '../../components/common/ReportTitle';
import { exportToCSV, reportHeaders } from '../../utils/exportUtils';
import dayjs from 'dayjs';
import PageLoader from '../../components/common/PageLoader';

const DEFAULT_LIMIT = 200;

const createDefaultFilters = () => {
  const today = dayjs().format('YYYY-MM-DD');
  return {
    from_date: today,
    to_date: today,
    department_name: '',
    worker_code: '',
    production_status: '',
    today_only: true,
    limit: DEFAULT_LIMIT
  };
};

const DepartmentWiseProductionReportPage = () => {
  const dispatch = useDispatch();
  const { report, completed_total_batch_count, loading, error } = useSelector(state => state.departmentWiseProductionReport);

  const [filters, setFilters] = useState(createDefaultFilters);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filteredReport, setFilteredReport] = useState([]);

  useEffect(() => {
    const params = {};
    if (filters.from_date) {
      params.from_date = filters.from_date;
    }
    if (filters.to_date) {
      params.to_date = filters.to_date;
    }
    if (filters.today_only !== undefined) {
      params.today_only = filters.today_only;
    }
    if (filters.limit !== undefined) {
      params.limit = filters.limit;
    }
    dispatch(getDepartmentWiseProductionReport(params));
  }, [dispatch, filters.from_date, filters.to_date, filters.today_only, filters.limit]);

  useEffect(() => {
    // Filter logic for department, worker, status (not date)
    let data = report || [];
    if (filters.department_name) {
      data = data.filter(row => (row.department_name || '').toLowerCase() === filters.department_name.toLowerCase());
    }
    if (filters.worker_code) {
      data = data.filter(row => row.worker_code === filters.worker_code);
    }
    if (filters.production_status) {
      data = data.filter(row => row.production_status === filters.production_status);
    }
    setFilteredReport(data);
  }, [report, filters]);

  const handleApplyFilters = (newFilters = {}) => {
    if (!newFilters || Object.keys(newFilters).length === 0) {
      setFilters(createDefaultFilters());
      setFilterOpen(false);
      return;
    }

    const mergedFilters = {
      ...filters,
      ...newFilters
    };

    if (!mergedFilters.from_date && !mergedFilters.to_date) {
      mergedFilters.today_only = true;
      mergedFilters.limit = DEFAULT_LIMIT;
    } else {
      mergedFilters.today_only = false;
      if (mergedFilters.limit === undefined || mergedFilters.limit === null) {
        mergedFilters.limit = DEFAULT_LIMIT;
      }
    }

    setFilters(mergedFilters);
    setFilterOpen(false);
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.department_name) count++;
    if (filters.worker_code) count++;
    if (filters.production_status) count++;
    if (filters.from_date) count++;
    if (filters.to_date) count++;
    return count;
  };

  const handleExport = () => {
    exportToCSV(filteredReport, 'department_wise_production_report', reportHeaders.departmentWiseProduction);
  };

  // Totals (example: total qty, total net weight, total pure weight)
  const totals = useMemo(() => {
    return {
      total_qty: filteredReport.reduce((sum, row) => sum + (parseFloat(row.qty) || 0), 0),
      total_net_weight: filteredReport.reduce((sum, row) => sum + (parseFloat(row.net_weight) || 0), 0),
      total_pure_weight: filteredReport.reduce((sum, row) => sum + (parseFloat(row.net_weight_purity) || 0), 0),
    };
  }, [filteredReport]);

  const handleLoadRecent = () => {
    setFilters(createDefaultFilters());
  };

  const handleLoadAll = () => {
    setFilters(prev => ({
      ...prev,
      today_only: false,
      limit: 0
    }));
  };

  return (
    <div style={{ padding: 24 }}>
      <PageLoader loading={loading} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <ReportTitle>Department Wise Production Report</ReportTitle>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={() => setFilterOpen(true)}
            sx={{
              position: 'relative',
              ...(getActiveFiltersCount() > 0 && {
                backgroundColor: 'primary.light',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'primary.main',
                }
              })
            }}
          >
            Filters
            {getActiveFiltersCount() > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  backgroundColor: 'error.main',
                  color: 'white',
                  borderRadius: '50%',
                  width: 20,
                  height: 20,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.75rem',
                  fontWeight: 'bold'
                }}
              >
                {getActiveFiltersCount()}
              </Box>
            )}
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleLoadRecent}
            disabled={loading}
          >
            Load Recent
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleLoadAll}
            disabled={loading}
          >
            Load All
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            sx={{ minWidth: 120 }}
          >
            Export CSV
          </Button>
        </Box>
      </div>
      <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
        <Card sx={{ minWidth: 180, bgcolor: 'primary.light', color: 'white' }}>
          <CardContent>
            <Typography variant="subtitle2" color="inherit">Total Qty</Typography>
            <Typography variant="h6" color="inherit">{totals.total_qty.toFixed(3)}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 180, bgcolor: 'info.light', color: 'white' }}>
          <CardContent>
            <Typography variant="subtitle2" color="inherit">Total Net Weight</Typography>
            <Typography variant="h6" color="inherit">{totals.total_net_weight.toFixed(3)}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 180, bgcolor: 'warning.light', color: 'white' }}>
          <CardContent>
            <Typography variant="subtitle2" color="inherit">Total Pure Weight</Typography>
            <Typography variant="h6" color="inherit">{totals.total_pure_weight.toFixed(3)}</Typography>
          </CardContent>
        </Card>
        {/* Completed Total Batch Count Card moved here */}
        <Card sx={{ minWidth: 180, bgcolor: 'success.light', color: 'white' }}>
          <CardContent>
            <Typography variant="subtitle2" color="inherit">Completed Total Batch Count</Typography>
            <Typography variant="h6" color="inherit">{completed_total_batch_count}</Typography>
          </CardContent>
        </Card>
      </div>
      <DepartmentWiseProductionReportTable reportData={filteredReport} loading={loading} />
      <DepartmentWiseProductionReportFilter open={filterOpen} onClose={() => setFilterOpen(false)} onApply={handleApplyFilters} initialFilters={filters} />
      {error && <Typography color="error">{error}</Typography>}
    </div>
  );
};

export default DepartmentWiseProductionReportPage; 